import React, { useState } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { resolvePhoto } from "@/lib/api";
import { Heart, X, ChevronLeft, ChevronRight, MapPin, Send } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function SwipeDeck({ candidates, onSwipe, onNote, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="aspect-[3/4.4] bg-white rounded-3xl border border-[#E5DFD3] flex items-center justify-center" data-testid="deck-loading">
        <span className="font-serif italic text-2xl text-[#913831]">loading…</span>
      </div>
    );
  }
  if (!candidates.length) {
    return (
      <div className="bg-white rounded-3xl border border-[#E5DFD3] p-10 text-center" data-testid="deck-empty">
        <h3 className="font-serif text-3xl text-[#1A1818]">That&apos;s everyone for now.</h3>
        <p className="text-sm text-[#737373] mt-3">Check back soon — we&apos;re slow on purpose.</p>
        <button onClick={onRefresh} data-testid="deck-refresh"
          className="mt-6 inline-flex items-center gap-2 border border-[#E5DFD3] hover:bg-[#F0EBE1] rounded-full px-5 py-2.5 text-sm font-medium">
          Refresh
        </button>
      </div>
    );
  }

  const top = candidates[0];
  const next = candidates[1];

  return (
    <div className="relative" data-testid="swipe-deck">
      <div className="relative aspect-[3/4.4]">
        {next && (
          <div key={next.id + "-bg"}
               className="absolute inset-0 scale-[0.96] translate-y-3 bg-white rounded-3xl border border-[#E5DFD3] shadow-[0_15px_40px_-15px_rgba(0,0,0,0.06)] overflow-hidden">
            <CardInner candidate={next} muted />
          </div>
        )}
        <AnimatePresence mode="popLayout">
          <SwipeCard key={top.id} candidate={top} onSwipe={onSwipe} />
        </AnimatePresence>
      </div>

      <div className="mt-6 flex items-center justify-center gap-4">
        <ActionButton variant="pass" testid="action-pass" onClick={() => onSwipe(top, "pass")}>
          <X size={26} strokeWidth={1.5} />
        </ActionButton>
        {onNote && (
          <ActionButton variant="note" testid="action-note" onClick={() => onNote(top)}>
            <Send size={18} strokeWidth={1.5} />
          </ActionButton>
        )}
        <ActionButton variant="like" testid="action-like" onClick={() => onSwipe(top, "like")}>
          <Heart size={24} strokeWidth={1.5} />
        </ActionButton>
      </div>
    </div>
  );
}

function ActionButton({ children, onClick, variant, testid }) {
  const sizeCls = variant === "note" ? "w-12 h-12" : "w-16 h-16";
  const style = variant === "like"
    ? "bg-[#913831] text-white hover:bg-[#7A2E28]"
    : variant === "note"
    ? "bg-[#F3E8DD] text-[#913831] border border-[#D4A373]/40 hover:bg-[#EADBC8]"
    : "bg-white text-[#1A1818] border border-[#E5DFD3] hover:bg-[#F0EBE1]";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`${sizeCls} rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 active:scale-90 ${style}`}>
      {children}
    </button>
  );
}

function SwipeCard({ candidate, onSwipe }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-15, 0, 15]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const passOpacity = useTransform(x, [-140, -40], [1, 0]);

  const handleEnd = (_, info) => {
    if (info.offset.x > 130) onSwipe(candidate, "like");
    else if (info.offset.x < -130) onSwipe(candidate, "pass");
  };

  return (
    <motion.div
      data-testid={`swipe-card-${candidate.id}`}
      className="absolute inset-0 bg-white rounded-3xl border border-[#E5DFD3] shadow-[0_25px_50px_-15px_rgba(0,0,0,0.15)] overflow-hidden cursor-grab active:cursor-grabbing"
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleEnd}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ x: x.get() > 0 ? 600 : -600, opacity: 0, transition: { duration: 0.35 } }}
    >
      <motion.div style={{ opacity: likeOpacity }}
        className="absolute top-6 right-6 z-20 px-3 py-1 border-2 border-[#913831] text-[#913831] rounded-md font-serif text-2xl italic rotate-[12deg]">
        yes
      </motion.div>
      <motion.div style={{ opacity: passOpacity }}
        className="absolute top-6 left-6 z-20 px-3 py-1 border-2 border-[#737373] text-[#737373] rounded-md font-serif text-2xl italic -rotate-[12deg]">
        not yet
      </motion.div>
      <CardInner candidate={candidate} />
    </motion.div>
  );
}

function CardInner({ candidate, muted }) {
  const [idx, setIdx] = useState(0);
  const photos = candidate.photos?.length ? candidate.photos : [];
  const photo = photos[idx];

  return (
    <div className="h-full flex flex-col p-3">
      <div className="relative flex-1 rounded-2xl overflow-hidden bg-[#F0EBE1]">
        {photo ? (
          <img src={resolvePhoto(photo)} alt={candidate.name}
            className="w-full h-full object-cover select-none pointer-events-none" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#737373] font-serif text-3xl">{candidate.name?.[0]}</div>
        )}
        {photos.length > 1 && !muted && (
          <>
            <div className="absolute top-3 left-3 right-3 flex gap-1.5">
              {photos.map((_, i) => (
                <span key={i} className={`flex-1 h-1 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`} />
              ))}
            </div>
            <button onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + photos.length) % photos.length); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/70 hover:bg-white rounded-full flex items-center justify-center text-[#1A1818]"
              data-testid="card-prev-photo">
              <ChevronLeft size={18} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % photos.length); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/70 hover:bg-white rounded-full flex items-center justify-center text-[#1A1818]"
              data-testid="card-next-photo">
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>
      <div className="px-2 pt-4 pb-1">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif text-3xl text-[#1A1818] leading-none flex items-center gap-2">
            <span>{candidate.name}<span className="text-[#737373] text-2xl">, {candidate.age}</span></span>
            {candidate.verified && <VerifiedBadge />}
          </h3>
          {candidate.location && (
            <span className="text-xs uppercase tracking-[0.2em] text-[#737373] flex items-center gap-1">
              <MapPin size={12} strokeWidth={1.5} /> {candidate.location}
              {candidate.distance_km != null && (
                <span className="ml-1 text-[#913831] normal-case tracking-normal">&middot; {candidate.distance_km} km</span>
              )}
            </span>
          )}
        </div>
        {candidate.bio && (
          <p className="mt-2 text-sm text-[#1A1818]/80 leading-relaxed line-clamp-3 italic font-serif text-base">
            &ldquo;{candidate.bio}&rdquo;
          </p>
        )}
        {candidate.interests?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {candidate.interests.slice(0, 4).map((t) => (
              <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-[#F0EBE1] text-[#1A1818] border border-[#E5DFD3]">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
